import React, { useMemo } from "react";
import { Bom, ReportAssignmentRow, ReportTypeDefinition } from "./types";
import { RaIcon } from "./RaIcon";

interface DataReadinessSectionProps {
  reportTypes: ReportTypeDefinition[];
  boms: Bom[];
  rows: ReportAssignmentRow[];
  onOpenStatus: (row: ReportAssignmentRow) => void;
  onOpenSetup: () => void;
}

/**
 * Section 3 — Is the data ready? A BOM x report-type matrix of
 * ready/waiting status, with a per-cell drill-down into each module.
 */
export const DataReadinessSection: React.FC<DataReadinessSectionProps> = ({
  reportTypes,
  boms,
  rows,
  onOpenStatus,
  onOpenSetup,
}) => {
  const readyCount = useMemo(() => rows.filter((r) => r.progress.ready).length, [rows]);

  return (
    <section className="sec sec-data">
      <div className="sh">
        <span className="num">3</span>
        <div>
          <h2>Is the data ready?</h2>
          <div className="sub">
            A report’s data is ready when every module in it has completed the report’s test
            group. Click a cell to see each module.
          </div>
        </div>
      </div>

      <div className="dlegend">
        <span className="dc flat rw">
          <RaIcon name="check" />
          Ready
        </span>
        <span className="dc flat wt">
          <RaIcon name="clock" />
          Waiting
        </span>
        <span className="lg-n">Click a status to see each module</span>
        <span className="dsum">
          <b>{readyCount}</b> of {rows.length} reports have data ready
        </span>
      </div>

      <div className="plan-wrap">
        <table className="plan dgrid">
          <thead>
            <tr>
              <th>Report</th>
              {boms.map((b) => (
                <th key={b.id} className="c">
                  {b.name}
                </th>
              ))}
              <th>Data ready when</th>
            </tr>
          </thead>
          <tbody>
            {reportTypes.map((type) => (
              <tr key={type.key}>
                <td className="rn">
                  <b>{type.name}</b>
                </td>
                {boms.map((b) => {
                  const cellRows = rows.filter((r) => r.typeKey === type.key && r.bomId === b.id);
                  if (!cellRows.length) {
                    return (
                      <td key={b.id} className="c">
                        <span className="dot n">–</span>
                      </td>
                    );
                  }
                  return (
                    <td key={b.id} className="c">
                      {cellRows.map((row) => {
                        const pg = row.progress;
                        if (pg.none) {
                          return (
                            <span
                              key={row.id}
                              className={`dc flat ${pg.ready ? "rw" : "wt"}`}
                              title="No modules to check. Ready once the BOM and production details are received."
                            >
                              <RaIcon name={pg.ready ? "check" : "clock"} />
                              {pg.ready ? "Ready" : "Waiting"}
                            </span>
                          );
                        }
                        if (!pg.total) {
                          return (
                            <span key={row.id} className="dc flat nomods">
                              No modules
                            </span>
                          );
                        }
                        return (
                          <button
                            key={row.id}
                            type="button"
                            className={`dc go ${pg.ready ? "rw" : "wt"}`}
                            title={`See the ${pg.total} modules`}
                            onClick={() => onOpenStatus(row)}
                          >
                            <RaIcon name={pg.ready ? "check" : "clock"} />
                            {pg.ready ? "Ready" : "Waiting"}{" "}
                            <span className="mono">
                              · {pg.done}/{pg.total}
                            </span>
                            <RaIcon name="chevRight" className="chv" />
                          </button>
                        );
                      })}
                    </td>
                  );
                })}
                <td className="drw">
                  {type.dataReadyCriterion}
                  {type.who === "none" ? "" : " complete"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ptot">
        <span className="lk">
          <RaIcon name="gear" />
          Each report type’s test group is picked once in Setup and applies to every project.
        </span>
        <span className="sp" />
        {/* <button type="button" className="lnk3" onClick={onOpenSetup}>
          Open setup
        </button> */}
      </div>
    </section>
  );
};
